/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/lookup_conditions_of_sale-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-lookup_conditions_of_sale-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/lookup_conditions_of_sale-entity-config.ts
 * - Run: pnpm run generate:api-clients (regenerates this file)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id
 * - Sortable fields: created_at, updated_at
 * - Required fields: id, code, name, sort_order, created_at, archived, updated_at
 * - Readonly fields: id, created_at, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/lookup_conditions_of_sale-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-lookup_conditions_of_sale-api-client.ts (API client)
 * - client/api/generated_schemas/lookup_conditions_of_sale.ts (TypeBox schemas and types)
 *
 * Source config: config/entities/lookup_conditions_of_sale-entity-config.ts
 * Public fields: 8/8 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LookupConditionsOfSaleClient, LookupConditionsOfSaleQueryOptions } from '../generated_clients/generated-lookup_conditions_of_sale-api-client.js';
import {
  LookupConditionsOfSaleInsert,
  LookupConditionsOfSaleUpdate,
  LookupConditionsOfSaleFieldConfig
} from '../../types/generated_schemas/generated-lookup_conditions_of_sale-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/lookup_conditions_of_sale_business_hooks/

// Query keys with enhanced caching strategy. A list key carries every
// parameter getAll sends, so two different pages never share a cache entry.
export const lookupConditionsOfSaleKeys = {
  all: ['lookup_conditions_of_sale'] as const,
  lists: () => [...lookupConditionsOfSaleKeys.all, 'list'] as const,
  list: (filters: Record<string, any>, page?: Pick<LookupConditionsOfSaleQueryOptions, 'limit' | 'offset' | 'sortBy' | 'sortOrder'>) =>
    [...lookupConditionsOfSaleKeys.lists(), { filters, ...page }] as const,
  details: () => [...lookupConditionsOfSaleKeys.all, 'detail'] as const,
  detail: (id: string) => [...lookupConditionsOfSaleKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...lookupConditionsOfSaleKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...lookupConditionsOfSaleKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...lookupConditionsOfSaleKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...lookupConditionsOfSaleKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useLookupConditionsOfSaleClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new LookupConditionsOfSaleClient();
};

/**
 * Hook to get all lookup_conditions_of_sale with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with lookup_conditions_of_sale data
 */
export const useLookupConditionsOfSaleList = (options?: LookupConditionsOfSaleQueryOptions) => {
  const client = useLookupConditionsOfSaleClient();
  
  return useQuery({
    queryKey: lookupConditionsOfSaleKeys.list(options?.filters || {}, {
      limit: options?.limit,
      offset: options?.offset,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
    }),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single lookup_conditions_of_sal by ID
 *
 * @param id - The lookup_conditions_of_sal ID
 * @returns React Query result with single lookup_conditions_of_sal data
 */
export const useLookupConditionsOfSale = (id: string) => {
  const client = useLookupConditionsOfSaleClient();
  
  return useQuery({
    queryKey: lookupConditionsOfSaleKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/lookup_conditions_of_sale
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/lookup_conditions_of_sale_business_hooks/
 */

/**
 * Hook to create a new lookup_conditions_of_sal
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, code, name, sort_order, created_at, archived, updated_at
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at
 *
 * @returns React Query mutation for creating lookup_conditions_of_sal
 */
export const useCreateLookupConditionsOfSale = () => {
  const client = useLookupConditionsOfSaleClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: LookupConditionsOfSaleInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupConditionsOfSaleKeys.all });
    },
  });
};

/**
 * Hook to update an existing lookup_conditions_of_sal
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at
 *
 * @returns React Query mutation for updating lookup_conditions_of_sal
 */
export const useUpdateLookupConditionsOfSale = () => {
  const client = useLookupConditionsOfSaleClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: LookupConditionsOfSaleUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: lookupConditionsOfSaleKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: lookupConditionsOfSaleKeys.lists() });
    },
  });
};

/**
 * Hook to delete a lookup_conditions_of_sal
 *
 * @returns React Query mutation for deleting lookup_conditions_of_sal
 */
export const useDeleteLookupConditionsOfSale = () => {
  const client = useLookupConditionsOfSaleClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupConditionsOfSaleKeys.all });
    },
  });
};

/**
 * Hook to count lookup_conditions_of_sale with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useLookupConditionsOfSaleCount = (filter?: Partial<Record<typeof LookupConditionsOfSaleFieldConfig.filterable[number], any>>) => {
  const client = useLookupConditionsOfSaleClient();
  
  return useQuery({
    queryKey: lookupConditionsOfSaleKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useLookupConditionsOfSaleFieldConfig = () => {
  return LookupConditionsOfSaleFieldConfig;
};